import {
  convertFromRaw, // genKey,
} from 'draft-js'
import type { BusinessData } from 'types/business'
import type {
  ButtonElement,
  Campaign,
  CampaignData,
  CampaignFooter,
  CampaignSection,
  CampaignSectionColumn,
  EmailElement,
  FooterElement,
  PlaylistElement,
  TextElement,
} from 'types/content-editor'
import { camelizeWord } from '@disco/utils/string-utils'
import { getTrackSubheader } from '@disco/utils/tracks'
import exportToText, {
  getDividerElement,
  getHeader,
  getStringOfChars,
} from './text-export'

const getTextElement = (element: TextElement) => {
  const elementContent = element.content && element.content
  if (!elementContent) return ''
  const resultString = '\n' + exportToText(convertFromRaw(elementContent || {}))
  return resultString
}

const getPlaylistElement = (element: PlaylistElement, data: CampaignData) => {
  if (element.playlist_id && data.playlists) {
    const playlist = data.playlists.find(
      p => p.playlist_id === element.playlist_id,
    )
    const header =
      !element.disableHeader && playlist ? getHeader(playlist.name) : ''

    if (playlist) {
      return `${header}
${playlist.tracks
  .map((t, index) => {
    const section = playlist.sections.find(s => s.position === index)
    const sectionRow = section
      ? `${(section.name ?? '').toUpperCase()}\n${getStringOfChars('-', 60)}\n`
      : ''
    const subheader = getTrackSubheader(t)
    const trackRow = `- ${t.name}${subheader ? ` <${subheader}>` : ''}\n`
    return sectionRow + trackRow
  })
  .join('')}
>> ${element.buttonText}\n${playlist.view_playlist_url}`
    }
  }

  return ''
}

const getImageElement = () => ''

const socialLinksToText = (input: Record<string, any>) => {
  const { ...socialObject } = input
  let result = ''

  for (const key in socialObject) {
    if (socialObject[key] && socialObject[key].trim().length > 0) {
      result += `${camelizeWord(key)}: ${socialObject[key]}\n`
    }
  }

  return result
}

const getFooterElement = (
  _element: FooterElement,
  _data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
) => {
  const social =
    footer && footer.social_links
      ? `${getHeader('Social:')}${socialLinksToText(
          footer.social_links,
        )}${getStringOfChars('-', 60)}\n\n`
      : ''
  const elementText =
    footer && footer.footer_text
      ? `${exportToText(convertFromRaw(footer.footer_text || {}))}\n`
      : ''
  const addressText =
    businessSettings && businessSettings.business_address
      ? `${exportToText(
          convertFromRaw(businessSettings.business_address || {}),
        )}\n`
      : ''
  return `${getDividerElement()}\n\n${social}${elementText}${addressText}Unsubscribe: {{unsubscribe_url}}`
}

const getButtonElement = (element: ButtonElement) => {
  return `>> ${element.caption}\n${element.link}`
}

const getElement = (
  element: EmailElement,
  _section: CampaignSection,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined, // campaign: Campaign,
) => {
  if (element.type === 'text') return getTextElement(element)
  if (element.type === 'image') return getImageElement()
  if (element.type === 'footer')
    return getFooterElement(element, data, businessSettings, footer)
  if (element.type === 'playlist') return getPlaylistElement(element, data)
  if (element.type === 'button') return getButtonElement(element)
  if (element.type === 'divider') return getDividerElement()
  throw new Error(
    `mjml getElement do not know element of type: ${(element as any).type}`,
  )
}

const getColumn = (
  column: CampaignSectionColumn,
  section: CampaignSection,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
) => {
  let columnContent = ''

  if (column.columnContent) {
    column.columnContent.forEach(element => {
      columnContent += getElement(
        element,
        section,
        data,
        businessSettings,
        footer,
      )
    })
  }

  return columnContent.length > 0 ? `${columnContent}\n` : ''
}

export function generateText(
  campaign: Campaign,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
): string {
  const { sections } = campaign
  let contents = ''
  sections.forEach(section => {
    let sectionColumns = ''
    section.columns.forEach(column => {
      sectionColumns += getColumn(
        column,
        section,
        data,
        businessSettings,
        footer,
      )
    })
    contents += sectionColumns.length > 0 ? `${sectionColumns}\n` : ''
  })
  return contents
}
